// src/OmicAtlas.jsx
import React, { useEffect, useRef, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPlus,
  faMagnifyingGlass,
  faChevronLeft,
  faChevronDown,
  faSun,
  faMoon,
  faCircleNodes,
  faPeopleGroup,
} from '@fortawesome/free-solid-svg-icons';
import './OmicAtlas.css';
import OmicsDiscoveries from './OmicsDiscoveries';
import CohortOverview from './CohortOverview';
import MessageRenderer from './MessageRenderer'; // <-- 1. IMPORT the new module

const API_BASE = 'https://service9007.szapfs.org';
const COOKIE_NAME = 'oa_sid';

function OmicAtlas() {
  const [userInput, setUserInput] = useState('');
  const [theme, setTheme] = useState('dark');
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [fade, setFade] = useState('none');
  const [showDiscoveries, setShowDiscoveries] = useState(false);
  const [showCohortOverview, setShowCohortOverview] = useState(false);

  const [conversations, setConversations] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loadingMsgs, setLoadingMsgs] = useState(false);
  const [sessionLabel, setSessionLabel] = useState('—');
  const composeRef = useRef(null);
  const [composePad, setComposePad] = useState(0);

  const FADE_MS = 140;
  const WIDTH_MS = 220;

  const showBottomComposer = !!activeId && messages.length > 0;
  const hasAnyMessages = messages.length > 0;

  const msgScrollRef = useRef(null);
  const postSeenRef = useRef(false);

  const R = {
    listChats: '/api/chats',
    createChat: '/api/chat',
    listMsgs: (id) => `/api/chats/${id}/messages`,
    sendMsg: (id) => `/api/chats/${id}/message`,
    streamMsg: (id) => `/api/chats/${id}/message/stream`,
    me: '/api/me',
  };

  // Streaming state for one turn
  const streamRef = useRef({
    phase: 'pre', // 'pre' | 'tool' | 'await_post' | 'done'
    toolName: null,
    sessionId: null,
    stepIndex: 0,
    asstPreId: null,
  });

  // --- helpers ---
  const api = async (path, opts = {}) => {
    const res = await fetch(`${API_BASE}${path}`, {
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      ...opts,
    });
    if (!res.ok) throw new Error(`${res.status}`);
    return res.json();
  };

  const readCookie = (name) => {
    const m = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
    return m ? decodeURIComponent(m[1]) : null;
  };

  // --- load conversations on mount ---
  useEffect(() => {
    (async () => {
      try {
        const data = await api(R.listChats);
        setConversations(data.chats || []);
      } catch (_) {}
      try {
        const me = await api(R.me);
        const tag = me?.label || '…????';
        const days = Number.isFinite(me?.days_left) ? `${me.days_left}d` : '?d';
        setSessionLabel(`${tag} · ${days}`);
      } catch (_) {
        setSessionLabel('…???? · ?d');
      }
    })();
  }, []);

  // --- open conversation ---
  const openConversation = async (id) => {
    setShowDiscoveries(false);
    setShowCohortOverview(false);
    if (!id) return;
    setActiveId(id);
    setLoadingMsgs(true);
    try {
      const data = await api(R.listMsgs(id));
      const msgs = (data.messages || []).map((m) => {
        if (m.meta && typeof m.meta === 'string') {
          try {
            m.meta = JSON.parse(m.meta);
          } catch {}
        }
        return m;
      });
      setMessages(msgs);
      const last = msgs.length ? msgs[msgs.length - 1].content || '' : '';
      setConversations((prev) => prev.map((c) => (c.id === id ? { ...c, last_message: last } : c)));
    } catch (_) {
      setMessages([]);
    } finally {
      setLoadingMsgs(false);
    }
  };

  // --- new chat ---
  const newChat = () => {
    setActiveId(null);
    setMessages([]);
    setUserInput('');
    setShowDiscoveries(false);
    setShowCohortOverview(false);
  };

  // --- send message (SSE stream) ---
  const sendMessage = async () => {
    const content = userInput.trim();
    if (!content) return;

    let convId = activeId;
    if (!convId) {
      try {
        const data = await api(R.createChat, {
          method: 'POST',
          body: JSON.stringify({ title: 'New chat' }),
        });
        convId = data.id;
        setActiveId(convId);
        setConversations((prev) => [
          {
            id: data.id,
            title: data.title,
            archived: 0,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
            last_message: '',
          },
          ...prev,
        ]);
      } catch (_) {
        return;
      }
    }

    // optimistic user msg
    const localUser = {
      id: `u_${Date.now()}`,
      role: 'user',
      content,
      model: null,
      latency_ms: null,
      created_at: new Date().toISOString(),
    };
    setMessages((prev) => [...prev, localUser]);
    setUserInput('');
    const targetId = convId;
    setConversations((prev) => prev.map((c) => (c.id === targetId ? { ...c, last_message: content } : c)));

    // PRE placeholder
    const asstPreId = `a_pre_${Date.now()}`;
    streamRef.current = { phase: 'pre', toolName: null, sessionId: null, stepIndex: 0, asstPreId };
    postSeenRef.current = false;
    setMessages((prev) => [
      ...prev,
      {
        id: asstPreId,
        role: 'assistant',
        content: '',
        model: null,
        latency_ms: null,
        created_at: new Date().toISOString(),
        awaitingDelta: true,
      },
    ]);

    try {
      const res = await fetch(`${API_BASE}${R.streamMsg(convId)}`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ content }),
      });
      if (!res.ok || !res.body) throw new Error('stream failed');

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buf = '';
      let preText = '';

      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });

        // allow for multiple SSE frames in the chunk
        const frames = buf.split('\n\n');
        buf = frames.pop() || '';

        for (const f of frames) {
          const line = f.trim();
          if (!line || !line.startsWith('data:')) continue;
          const payload = line.slice(5).trim();
          if (!payload || payload === '[DONE]') continue;

          let obj;
          try {
            obj = JSON.parse(payload);
          } catch {
            continue;
          }

          // Upstream error
          if (obj.error || obj.message) {
            const msg = obj.message || obj.error;
            setMessages((prev) =>
              prev.map((m) =>
                m.id === streamRef.current.asstPreId ? { ...m, content: `[Error] ${msg}`, awaitingDelta: false } : m
              )
            );
            continue;
          }

          // TOOL EVENTS (started / progress / completed)
          if (obj.tool) {
            const { status, name, step, bytes, message, path, info, output, id: toolId, session } = obj.tool;

            if (status === 'started') {
              streamRef.current.phase = 'tool';
              streamRef.current.toolName = name || 'tool';
              // use provided id if present; else synth
              const sessId = session || toolId || `tool_${Date.now()}`;
              streamRef.current.sessionId = sessId;
              streamRef.current.stepIndex = 0;

              setMessages((prev) => [
                ...prev,
                {
                  id: sessId,
                  role: 'assistant',
                  content: `Initializing…`,
                  meta: { internal: 1, tool: streamRef.current.toolName, kind: 'tool_session', session: sessId },
                  created_at: new Date().toISOString(),
                },
              ]);
              continue;
            }

            if (status === 'progress') {
              const idFromSrv = obj.tool.id; // server DB id (authoritative)
              const lineTxt =
                `${step || 'step'}` +
                (Number.isFinite(bytes) ? ` (${bytes} bytes)` : '') +
                (message ? ` — ${message}` : '') +
                (path ? ` [${path}]` : '') +
                (info ? ` ${JSON.stringify(info)}` : '');
            
              setMessages(prev => {
                const exists = prev.find(m => m.id === idFromSrv);
                if (exists) {
                  // either replace line OR append extra payload if provided
                  if (typeof obj.tool.append === 'string' && obj.tool.append.length) {
                    return prev.map(m => m.id === idFromSrv ? { ...m, content: (m.content || '') + '\n\n' + obj.tool.append } : m);
                  }
                  return prev.map(m => m.id === idFromSrv ? { ...m, content: lineTxt } : m);
                }
                // create the row if we haven't seen it yet
                return [
                  ...prev,
                  {
                    id: idFromSrv || `tool_${Date.now()}`, // rare fallback
                    role: 'assistant',
                    content: lineTxt,
                    meta: { internal: 1, tool: streamRef.current.toolName, kind: 'tool_step', session: streamRef.current.sessionId, step: step || null },
                    created_at: new Date().toISOString()
                  }
                ];
              });
              continue;
            }
            

            if (status === 'completed') {
              const doneId = `${streamRef.current.sessionId}_done`;
              setMessages((prev) => [
                ...prev,
                {
                  id: doneId,
                  role: 'assistant',
                  content: `✅ Tool done: ${streamRef.current.toolName}${
                    output?.path ? ` (saved: ${output.path})` : ''
                  }`,
                  meta: { internal: 1, tool: streamRef.current.toolName, kind: 'tool_done', session: streamRef.current.sessionId },
                  created_at: new Date().toISOString(),
                },
              ]);
              streamRef.current.phase = 'await_post';
              continue;
            }
          }

          // FINAL POST (single frame with full content) — unified handler
          if (obj.post && typeof obj.post.content === 'string') {
            const postId = obj.post.id || `post_${Date.now()}`;
            const postContent = obj.post.content;
            setMessages((prev) => [
              ...prev,
              {
                id: postId,
                role: 'assistant',
                content: postContent,
                model: null,
                latency_ms: null,
                created_at: new Date().toISOString(),
              },
            ]);
            setConversations((prev) =>
              prev.map((c) => (c.id === targetId ? { ...c, last_message: postContent } : c))
            );
            streamRef.current.phase = 'done';
            postSeenRef.current = true;
            continue;
          }

          // PRE deltas only before tool
          if (obj.delta) {
            if (streamRef.current.phase === 'pre') {
              preText += obj.delta;
              setMessages((prev) =>
                prev.map((m) =>
                  m.id === streamRef.current.asstPreId
                    ? { ...m, content: (m.content || '') + obj.delta, awaitingDelta: false }
                    : m
                )
              );
            }
          }

          // stream completion signal
          if (obj.done) {
            // fetch once if we missed the post frame
            if (!postSeenRef.current) {
              try {
                const data = await api(R.listMsgs(targetId));
                const msgs = (data.messages || []).map((m) => {
                  if (m.meta && typeof m.meta === 'string') {
                    try {
                      m.meta = JSON.parse(m.meta);
                    } catch {}
                  }
                  return m;
                });
                setMessages(msgs);
                const last = msgs.length ? msgs[msgs.length - 1].content || '' : '';
                setConversations((prev) =>
                  prev.map((c) => (c.id === targetId ? { ...c, last_message: last } : c))
                );
              } catch (_) {}
            }
            // stop typing dots on pre bubble
            setMessages((prev) =>
              prev.map((m) =>
                m.id === streamRef.current.asstPreId ? { ...m, awaitingDelta: false } : m
              )
            );
          }
        }
      }

      // Network closed without {done:true}? Fallback fetch once.
      if (!postSeenRef.current) {
        try {
          const data = await api(R.listMsgs(targetId));
          const msgs = (data.messages || []).map((m) => {
            if (m.meta && typeof m.meta === 'string') {
              try {
                m.meta = JSON.parse(m.meta);
              } catch {}
            }
            return m;
          });
          setMessages(msgs);
          const last = msgs.length ? msgs[msgs.length - 1].content || '' : '';
          setConversations((prev) =>
            prev.map((c) => (c.id === targetId ? { ...c, last_message: last } : c))
          );
        } catch (_) {}
      }

      // bump convo to top
      setConversations((prev) => {
        const idx = prev.findIndex((c) => c.id === convId);
        if (idx === -1) return prev;
        const clone = [...prev];
        const item = { ...clone[idx], updated_at: new Date().toISOString() };
        clone.splice(idx, 1);
        return [item, ...clone];
      });
    } catch (_) {}
  };

  // scroll to bottom when messages change
  useEffect(() => {
    if (msgScrollRef.current) {
      msgScrollRef.current.scrollTop = msgScrollRef.current.scrollHeight;
    }
  }, [messages]);

  // also re-scroll when the compose padding changes
  useEffect(() => {
    if (msgScrollRef.current) {
      msgScrollRef.current.scrollTop = msgScrollRef.current.scrollHeight;
    }
  }, [composePad]);

  // measure bottom composer
  useEffect(() => {
    const measure = () => {
      const height = composeRef.current ? composeRef.current.offsetHeight : 0;
      setComposePad(showBottomComposer ? height + 16 : 0);
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [showBottomComposer]);

  // sidebar collapse anim
  const collapse = () => {
    if (sidebarCollapsed) return;
    setFade('out');
    setTimeout(() => {
      setSidebarCollapsed(true);
      setFade('none');
    }, FADE_MS);
  };
  const expand = () => {
    if (!sidebarCollapsed) return;
    setSidebarCollapsed(false);
    setTimeout(() => setFade('in'), WIDTH_MS);
    setTimeout(() => setFade('none'), WIDTH_MS + FADE_MS);
  };

  // enter to send
  const onKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const heroHidden = showBottomComposer;

  return (
    <div className="OATS-container" data-theme={theme}>
      <div className="OATS-shell" data-sidebar={sidebarCollapsed ? 'collapsed' : 'expanded'} data-fade={fade}>
        <aside className="OATS-sidebar">
          <div className="OATS-sidebar-brand">
            <div
              className="OATS-brand-logo"
              role="button"
              tabIndex={0}
              onClick={sidebarCollapsed ? expand : undefined}
              onKeyDown={(e) => {
                if ((e.key === 'Enter' || e.key === ' ') && sidebarCollapsed) expand();
              }}
              aria-label={sidebarCollapsed ? 'Expand sidebar' : 'Omics Atlas'}
              title={sidebarCollapsed ? 'Expand' : 'Omics Atlas'}
              data-clickable={sidebarCollapsed ? 'yes' : 'no'}
            >
              OA
            </div>

            {!sidebarCollapsed && (
              <button
                type="button"
                className="OATS-collapse-btn"
                onClick={collapse}
                aria-label="Collapse sidebar"
                title="Collapse"
              >
                <FontAwesomeIcon icon={faChevronLeft} />
              </button>
            )}
          </div>

          {/* Full-width CTAs */}
          <div className="OATS-sidebar-top OATS-fadable">
            <div className="OATS-top-actions">
              <button 
                className="OATS-cta" 
                type="button" 
                title={showDiscoveries || showCohortOverview ? "New Chat" : "Start new chat"} 
                onClick={newChat}
              >
                <FontAwesomeIcon className="OATS-btn-icon" icon={faPlus} />
                <span className="OATS-btn-label">New Chat</span>
              </button>

              <button className="OATS-cta" type="button" title="Omics Discoveries" onClick={() => {setShowDiscoveries(true); setShowCohortOverview(false)}}>
                <FontAwesomeIcon className="OATS-btn-icon" icon={faCircleNodes} />
                <span className="OATS-btn-label">Omics Discoveries</span>
              </button>

              <button className="OATS-cta" type="button" title="Cohort Overview" onClick={() => {setShowCohortOverview(true); setShowDiscoveries(false)}}>
                <FontAwesomeIcon className="OATS-btn-icon" icon={faPeopleGroup} />
                <span className="OATS-btn-label">Cohort Overview</span>
              </button>
              
              <button className="OATS-cta" type="button" title="Sök i chattar">
                <FontAwesomeIcon className="OATS-btn-icon" icon={faMagnifyingGlass} />
                <span className="OATS-btn-label">Search Chats</span>
              </button>
              
            </div>
          </div>

          <div className="OATS-separator OATS-fadable" />

          {/* Conversations list */}
          <div className="OATS-sidebar-scroller OATS-fadable">
            <div className="OATS-section">
              <div className="OATS-section-title">Chats</div>
              {conversations.map((c) => (
                <button
                  key={c.id}
                  className={`OATS-list-item ${activeId === c.id ? 'is-active' : ''}`}
                  type="button"
                  title={c.title || 'Untitled'}
                  onClick={() => openConversation(c.id)}
                >
                  <span className="OATS-list-label">{c.last_message || c.title || 'Untitled'}</span>
                </button>
              ))}
              {!conversations.length && <div className="OATS-empty-hint">No conversations yet</div>}
            </div>
          </div>

          {/* Footer with session label */}
          <div className="OATS-sidebar-footer">
            <div className="OATS-user">
              <div className="OATS-avatar">OA</div>
              <div className="OATS-user-meta OATS-fadable">
                <div className="OATS-user-name">Omics Atlas</div>
                <div className="OATS-user-plan">Sess: {sessionLabel}</div>
              </div>
            </div>
          </div>
        </aside>

        <main className="OATS-content">
          {showDiscoveries ? (
            <OmicsDiscoveries theme={theme} />
          ) : showCohortOverview ? (
            <CohortOverview theme={theme} />
          ) : (
            <>
              <header className="OATS-topbar">
                <div className="OATS-topbar-right">
                  <div className="OATS-topbrand">
                    <span className="OATS-brand-name">MONAD v1.52</span>
                    <span className="OATS-pro-badge">Preview</span>
                    <FontAwesomeIcon className="OATS-caret" icon={faChevronDown} aria-hidden />
                  </div>
                  <button
                    type="button"
                    className="OATS-theme-btn"
                    onClick={() => setTheme((t) => (t === 'dark' ? 'light' : 'dark'))}
                    aria-pressed={theme === 'light'}
                    aria-label="Toggle theme"
                    title={theme === 'light' ? 'Switch to dark mode' : 'Switch to light mode'}
                  >
                    <FontAwesomeIcon icon={theme === 'light' ? faSun : faMoon} />
                  </button>
                </div>
              </header>

          {/* HERO (hidden once a thread exists) */}
          <section className={`OATS-hero ${heroHidden ? 'OATS-hero--hidden' : ''}`}>
            <h1 className="OATS-hero-title">Introducing MONAD AI</h1>
            <p className="OATS-hero-sub">
              <span style={{ display: 'inline-block', marginBottom: '1vh' }}>
                <b>M</b>ulti-<b>O</b>mics <b>N</b>etwork <b>A</b>nalysis & <b>D</b>iscovery
              </span>
              <br />
              AI-driven analysis framework of multi-omics networks to accelerate discovery
            </p>

            <div className="OATS-search">
              <div className="OATS-search-left" aria-hidden>
                <FontAwesomeIcon className="OATS-caret" icon={faPlus} aria-hidden />
              </div>
              <input
                className="OATS-search-input"
                type="text"
                placeholder="Ask Anything..."
                value={userInput}
                onChange={(e) => setUserInput(e.target.value)}
                onKeyDown={onKeyDown}
              />
            </div>
          </section>

          {/* MESSAGES */}
          {hasAnyMessages && (
            <div className="OATS-chat" ref={msgScrollRef} style={{ paddingBottom: composePad }}>
              <div className="OATS-messages">
                {loadingMsgs && <div className="OATS-loading"></div>}
                {messages.map((m) => {
  const meta =
    m.meta && typeof m.meta === 'string'
      ? (() => {
          try {
            return JSON.parse(m.meta);
          } catch {
            return {};
          }
        })()
      : m.meta;
  const isInternal = meta && (meta.internal === 1 || meta.internal === true);

  return (
    <div
      key={m.id}
      className={`OATS-message ${
        m.role === 'user'
          ? 'OATS-message--user'
          : isInternal
          ? 'OATS-message--internal'
          : 'OATS-message--assistant'
      }`}
    >
      <div className={isInternal ? 'OATS-tool-block' : 'OATS-message-bubble'}>
        <div className="OATS-message-html">
          {/* 2. REPLACE the old logic with the new component */}
          <MessageRenderer content={m.content} />
        </div>
      </div>

      {m.latency_ms != null && !isInternal && (
        <div className="OATS-message-meta">{m.latency_ms ? `${m.latency_ms}ms` : ''}</div>
      )}
    </div>
  );
})}

              </div>
            </div>
          )}

          {/* BOTTOM COMPOSER */}
          <div className={`OATS-compose ${showBottomComposer ? 'OATS-compose--active' : ''}`} ref={composeRef}>
            <div className="OATS-search">
              <div className="OATS-search-left" aria-hidden>
                <FontAwesomeIcon className="OATS-caret" icon={faPlus} aria-hidden />
              </div>
              <input
                className="OATS-search-input"
                type="text"
                placeholder="Skriv ett meddelande"
                value={userInput}
                onChange={(e) => setUserInput(e.target.value)}
                onKeyDown={onKeyDown}
              />
            </div>
          </div>
            </>
          )}
        </main>
      </div>
    </div>
  );
}

export default OmicAtlas;
